import React, { useState, useEffect, useCallback } from "react";
import { fetchTVGuide } from "../utilities/dataSources";
import {mobile} from "../services/mobileBridge";
import "../App.css";

const HANGMAN_STAGES = [
  `
      ┌─────
      │    │
      │
      │
      │
      │
    ──┴──
  `,
  `
      ┌─────
      │    │
      │    O
      │
      │
      │
    ──┴──
  `,
  `
      ┌─────
      │    │
      │    O
      │    │
      │
      │
    ──┴──
  `,
  `
      ┌─────
      │    │
      │    O
      │   ╲│
      │
      │
    ──┴──
  `,
  `
      ┌─────
      │    │
      │    O
      │   ╲│╱
      │
      │
    ──┴──
  `,
  `
      ┌─────
      │    │
      │    O
      │   ╲│╱
      │    │
      │
    ──┴──
  `,
  `
      ┌─────
      │    │
      │    O
      │   ╲│╱
      │    │
      │   ╱ ╲
    ──┴──
  `,
];

const FALLBACK_WORDS = ["MOVIE", "CINEMA", "SCREEN", "ACTOR", "PLOT"];

function extractMovieTitles(tvGuide: any): string[] {
  const titles = new Set<string>();
  const dates = Object.keys(tvGuide).filter(k => /\d{4}-\d{2}-\d{2}/.test(k));
  const mapping: Record<string, string> = tvGuide.ProgramMapping || {};
  const target = ["104", "106"];

  for (const date of dates) {
    const channels = tvGuide[date]?.Channels || [];
    for (const ch of channels) {
      if (!target.includes(ch.Number)) continue;
      for (const prog of ch.Programmes || []) {
        const name = mapping[prog.TitleId];
        if (!name) continue;
        const cleaned = name.toUpperCase().replace(/[^A-Z\s]/g, "").trim();
        if (cleaned.length >= 4 && /^[A-Z\s]+$/.test(cleaned)) {
          titles.add(cleaned);
        }
      }
    }
  }

  return Array.from(titles);
}

function Hangman({ country }: { country: 'south-africa' | 'nigeria' }) {
  const [word, setWord] = useState("");
  const [movieTitles, setMovieTitles] = useState<string[]>([]);
  const [guessedLetters, setGuessedLetters] = useState<Set<string>>(new Set());
  const [wrongGuesses, setWrongGuesses] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sourceText, setSourceText] = useState("");

  const maxWrong = HANGMAN_STAGES.length - 1;

  const pickWord = useCallback((titles: string[]) => {
    const pool = titles.length > 0 ? titles : FALLBACK_WORDS;
    const w = pool[Math.floor(Math.random() * pool.length)];
    setWord(w);
    setSourceText(titles.length > 0 ? "Movie Title • Ch 104/106" : "Fallback Word");
  }, []);

  const startNewGame = useCallback(() => {
    setGuessedLetters(new Set());
    setWrongGuesses(0);
    setGameOver(false);
    setWon(false);
    pickWord(movieTitles);
    setGameStarted(true);
  }, [movieTitles, pickWord]);

  useEffect(() => {
    setLoading(true);
    fetchTVGuide(country).then(res => {
      const titles = extractMovieTitles(res.data);
      setMovieTitles(titles);
      const pool = titles.length > 0 ? titles : FALLBACK_WORDS;
      const w = pool[Math.floor(Math.random() * pool.length)];
      setWord(w);
      setSourceText(titles.length > 0 ? "Movie Title • Ch 104/106" : "Fallback Word");
      setGameStarted(true);
    }).catch(() => {
      const w = FALLBACK_WORDS[Math.floor(Math.random() * FALLBACK_WORDS.length)];
      setWord(w);
      setSourceText("Fallback Word");
      setGameStarted(true);
    }).finally(() => setLoading(false));
  }, [country]);

  const handleGuess = (letter: string) => {
    if (letter === " ") return;
    if (guessedLetters.has(letter) || gameOver || won) return;
    const newGuessed = new Set(guessedLetters);
    newGuessed.add(letter);
    setGuessedLetters(newGuessed);
    if (!word.includes(letter)) {
      const nw = wrongGuesses + 1;
      setWrongGuesses(nw);
      mobile.haptic(nw >= maxWrong ? "gameover" : "warning");
      if (nw >= maxWrong) setGameOver(true);
    } else {
      const uniqueLetters = new Set(word.split(""));
      uniqueLetters.delete(" ");
      const allSet = Array.from(uniqueLetters).every(l => newGuessed.has(l));
      if (allSet) {
        mobile.haptic("success");
        setWon(true);
      }
    }
  };

  const getDisplayWord = () =>
    word.split("").map(l => {
      if (l === " ") return `<span class="letter-slot revealed space-slot">&nbsp;</span>`;
      if (guessedLetters.has(l)) {
        return `<span class="letter-slot revealed">${l}</span>`;
      }
      return `<span class="hangman-word-blank">_</span>`;
    }).join(" ");

  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const hearts = Array.from({ length: maxWrong }, (_, i) => i < maxWrong - wrongGuesses);

  return (
    <div className="container" style={{ padding: "0 16px", maxWidth: 720, margin: "0 auto" }}>
      <a href={`/game-studio/${country}`} className="game-back-link">&larr; Back to Games</a>
      <h1 style={{
        margin: "8px 0 2px",
        fontSize: "1.5rem",
        fontWeight: 800,
        background: "linear-gradient(120deg, var(--text-0), var(--accent))",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        WebkitTextFillColor: "transparent",
      }}>🎬 Movie Hangman</h1>
      <p style={{ color: "var(--text-3)", fontSize: "0.78rem", margin: "0 0 16px", fontWeight: 600, letterSpacing: "0.04em" }}>{sourceText}</p>

      {loading && (
        <div style={{ color: "var(--accent)", margin: "40px 0", fontWeight: 600 }}>
          Loading movie titles<span className="loading-dots" />
        </div>
      )}

      {!loading && gameStarted && (
        <div className="hangman-content">
          {/* Left: Gallows + Hearts */}
          <div className="hangman-drawing">
            <pre style={{ fontSize: "15px", lineHeight: 1.35 }}>
              {HANGMAN_STAGES[wrongGuesses]}
            </pre>
            <div className="hangman-hearts">
              {hearts.map((alive, i) => (
                <span key={i} className={`hangman-heart ${alive ? "" : "lost"}`}>❤</span>
              ))}
            </div>
            <div className="hangman-status">
              Wrong: <span>{wrongGuesses}</span> / {maxWrong}
            </div>
          </div>

          {/* Right: Word + Keyboard */}
          <div className="hangman-game">
            <div className="hangman-section-label">Guess the title</div>
            <div className="hangman-word">
              <h2 dangerouslySetInnerHTML={{ __html: getDisplayWord() }} />
            </div>

            {!gameOver && !won && (
              <>
                <div className="hangman-section-label">Pick a letter</div>
                <div className="hangman-buttons">
                  {alphabet.map(letter => (
                    <button
                      key={letter}
                      onClick={() => handleGuess(letter)}
                      disabled={guessedLetters.has(letter) || gameOver || won}
                      className={`letter-btn ${
                        guessedLetters.has(letter)
                          ? word.includes(letter) ? "correct" : "wrong"
                          : ""
                      }`}
                    >
                      {letter}
                    </button>
                  ))}
                </div>
              </>
            )}

            {gameOver && (
              <div className="hangman-overlay">
                <div className="hangman-message">
                  <h2 style={{ color: "var(--live)" }}>💀 Game Over</h2>
                  <p>The title was</p>
                  <strong>{word}</strong>
                  <div style={{ marginTop: 16 }}>
                    <button className="center-btn" onClick={startNewGame}>🔄 Try Again</button>
                  </div>
                </div>
              </div>
            )}

            {won && (
              <div className="hangman-overlay">
                <div className="hangman-message">
                  <h2 style={{ color: "var(--good)" }}>🎉 Solved!</h2>
                  <p>You guessed</p>
                  <strong>{word}</strong>
                  <div style={{ marginTop: 16 }}>
                    <button className="center-btn" onClick={startNewGame}>🔄 Next Title</button>
                  </div>
                </div>
              </div>
            )}

            <button className="hangman-new-btn" onClick={startNewGame} style={{ marginTop: 12 }}>
              🎲 New Title
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Hangman;
